/**
 * TREND LINE CHART
 *
 * Plots pass-rate over time from a TrendResponse.buckets[] payload.
 * A simple single-series line chart with a reference dashed line for the
 * period-wide average pass rate.
 *
 * Props:
 *   - buckets: Array<{ bucketStart: ISODate, passRate: number }>
 *   - height: number (default 240)
 *   - onPointClick: optional (bucket) => void — fired when the user clicks a
 *     line node; used by the parent to open the drill-down panel for that day
 */

import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid
} from 'recharts'
import { token } from '@atlaskit/tokens'

const formatDay = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null
  const { bucketStart, passRate, totalExecutions } = payload[0].payload
  return (
    <div
      style={{
        backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
        border: `1px solid ${token('color.border', '#DFE1E6')}`,
        borderRadius: '3px',
        padding: '8px 10px',
        fontSize: '12px',
        boxShadow: '0 2px 6px rgba(9, 30, 66, 0.25)'
      }}
    >
      <div style={{ fontWeight: 600, color: token('color.text', '#172B4D') }}>
        {new Date(bucketStart).toLocaleDateString(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })}
      </div>
      <div style={{ color: token('color.text.subtle', '#6B778C'), marginTop: '4px' }}>
        Pass rate: <strong>{Number(passRate).toFixed(1)}%</strong>
      </div>
      <div style={{ color: token('color.text.subtle', '#6B778C') }}>
        Executions: {totalExecutions}
      </div>
    </div>
  )
}

const TrendLineChart = ({ buckets = [], height = 240, onPointClick }) => {
  const data = useMemo(() => buckets || [], [buckets])

  const avgPassRate = useMemo(() => {
    if (!data.length) return null
    const sum = data.reduce((acc, b) => acc + (b.passRate || 0), 0)
    return sum / data.length
  }, [data])

  if (!data.length) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: token('color.text.subtle', '#6B778C'),
          fontSize: '13px'
        }}
      >
        No executions yet — the trend chart will populate once tests start running.
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={data}
        margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
        onClick={(e) => {
          // Recharts passes the clicked point as activePayload[0].payload
          if (onPointClick && e && e.activePayload?.length) {
            onPointClick(e.activePayload[0].payload)
          }
        }}
      >
        <CartesianGrid stroke={token('color.border', '#DFE1E6')} strokeDasharray="3 3" />
        <XAxis
          dataKey="bucketStart"
          tickFormatter={formatDay}
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
        />
        <YAxis
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
        />
        <Tooltip content={<CustomTooltip />} />
        {avgPassRate !== null && (
          <ReferenceLine
            y={avgPassRate}
            stroke={token('color.text.subtle', '#6B778C')}
            strokeDasharray="4 4"
            label={{
              value: `Avg ${avgPassRate.toFixed(1)}%`,
              position: 'insideTopRight',
              fontSize: 10,
              fill: token('color.text.subtle', '#6B778C')
            }}
          />
        )}
        <Line
          type="monotone"
          dataKey="passRate"
          stroke={token('color.background.success.bold', '#22A06B')}
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5, style: { cursor: onPointClick ? 'pointer' : 'default' } }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

export default TrendLineChart
